/**
 * Filter bar for the pilot list.
 *
 * Every control writes to the URL rather than to local state, so a filtered
 * view is linkable — "the blocked pilots" is a thing one person sends another,
 * which is impossible if the filters live in memory.
 *
 * `overdue` and `blocked` are **toggles, not selects**: they answer the one
 * question this list exists for — which pilots need someone — and a
 * three-state dropdown ("any / yes / no") would bury a yes/no behind two
 * clicks. They are stored as the string `"true"`, because the URL is the state
 * and the API reads them as `stringbool`.
 *
 * Below 768px the bar becomes a bottom sheet with an applied-count badge
 * (plan §7.3): five inline controls at 375px leave no room for the list.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, §7.3
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PILOT_STATUSES } from '../../../components/domain/pilot-status';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { filterHandler } from '../../../lib/filter-handler';

export interface PilotFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

/**
 * A yes/no filter.
 *
 * `aria-pressed` rather than a checkbox: it is a control that changes what the
 * list shows immediately, not a value to be submitted, and a screen reader
 * should hear its state as pressed rather than checked.
 */
function ToggleFilter({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <Button
      variant={active ? 'primary' : 'secondary'}
      size="sm"
      aria-pressed={active}
      onClick={onToggle}
    >
      {label}
    </Button>
  );
}

/** The controls, shared by the inline bar and the sheet. */
function FilterControls({
  filters,
  onFilterChange,
}: Omit<PilotFiltersProps, 'onClear'>): React.JSX.Element {
  const { t } = useTranslation();

  /** Flips a boolean filter, clearing it rather than storing `"false"`. */
  const toggle = (key: string): void => {
    onFilterChange(key, filters[key] === 'true' ? '' : 'true');
  };

  return (
    <>
      <label className="flex-1">
        <span className="sr-only">{t('pilot.filters.search')}</span>
        <Input
          type="search"
          value={filters['q'] ?? ''}
          onChange={(event) => {
            onFilterChange('q', event.target.value);
          }}
          placeholder={t('pilot.filters.searchPlaceholder')}
        />
      </label>

      <Select
        value={filters['status']}
        onValueChange={filterHandler('status', onFilterChange)}
        options={PILOT_STATUSES.map((status) => ({
          value: status,
          label: t(`pilot.status.${status}`),
        }))}
        placeholder={t('pilot.filters.anyStatus')}
        ariaLabel={t('pilot.filters.status')}
        className="sm:w-48"
      />

      <ToggleFilter
        label={t('pilot.filters.overdue')}
        active={filters['overdue'] === 'true'}
        onToggle={() => {
          toggle('overdue');
        }}
      />

      <ToggleFilter
        label={t('pilot.filters.blocked')}
        active={filters['blocked'] === 'true'}
        onToggle={() => {
          toggle('blocked');
        }}
      />
    </>
  );
}

export function PilotFilters({
  filters,
  onFilterChange,
  onClear,
}: PilotFiltersProps): React.JSX.Element {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const appliedCount = Object.keys(filters).length;

  return (
    <>
      {/* Inline bar, 768px and up. */}
      <div className="gap-s2 hidden items-center md:flex">
        <FilterControls filters={filters} onFilterChange={onFilterChange} />
        {appliedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('pilot.filters.clear')}
          </Button>
        ) : null}
      </div>

      {/* Below 768px: one button opening a sheet, with the applied count on it
          so active filters are visible without opening it. */}
      <div className="md:hidden">
        <Button
          variant="secondary"
          onClick={() => {
            setSheetOpen(true);
          }}
          className="w-full"
        >
          <Icon name="search" />
          {appliedCount === 0
            ? t('actions.filter')
            : t('pilot.filters.applied', { count: appliedCount })}
        </Button>

        <Dialog
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t('actions.filter')}
          footer={
            <>
              <Button variant="ghost" onClick={onClear}>
                {t('pilot.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t('actions.filter')}
              </Button>
            </>
          }
        >
          <div className="gap-s3 flex flex-col">
            <FilterControls filters={filters} onFilterChange={onFilterChange} />
          </div>
        </Dialog>
      </div>
    </>
  );
}
